import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import styles from "./index.module.scss";
import { getFileHeader, matchesSignature } from "../../../../../shared/ui/FileInput";
import { SIGNATURES } from "../../../../../shared/consts";

// HEIC намеренно НЕ указываем: без него iOS сам конвертирует фото в JPEG при загрузке,
// а с ним Safari отдаёт настоящий HEIC, который трейдер в Chrome не откроет. Так фото
// с iPhone приходят как JPEG без libheif и конвертации на бэкенде.
const ACCEPT = "image/png,image/jpeg,image/jpg,application/pdf";
const ALLOWED_SIGNATURES = [
  ...SIGNATURES.png,
  ...SIGNATURES.jpg,
  ...SIGNATURES.pdf,
];
const MAX_SIZE_MB = 10;

interface IUploadZone {
  files: File[];
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
}

export const UploadZone: React.FC<IUploadZone> = ({
  files,
  onAdd,
  onRemove,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setDragOver] = useState(false);
  const [warn, setWarn] = useState("");

  // Blob-URL превью создаём один раз на файл и revoke'аем при размонтировании/смене списка,
  // иначе URL.createObjectURL в render течёт (новый URL на каждый ре-рендер без освобождения).
  const [previews, setPreviews] = useState<string[]>([]);
  useEffect(() => {
    const urls = files.map((file) =>
      file.type.startsWith("image/") ? URL.createObjectURL(file) : ""
    );
    setPreviews(urls);
    return () => urls.forEach((url) => url && URL.revokeObjectURL(url));
  }, [files]);

  const validate = async (incoming: File[]): Promise<File[]> => {
    const valid: File[] = [];
    for (const file of incoming) {
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        setWarn(`Файл больше ${MAX_SIZE_MB} МБ`);
        continue;
      }
      // Сверяем по префиксу сигнатуры (JPEG = FF D8 FF, без учёта 4-го байта). Окончательную
      // проверку типа делает бэкенд — здесь это только быстрый фидбек клиенту.
      const header = await getFileHeader(file);
      if (matchesSignature(header, ALLOWED_SIGNATURES)) {
        valid.push(file);
      } else {
        setWarn("Загрузите изображение (PNG, JPG) или PDF");
      }
    }
    return valid;
  };

  const handleFiles = async (list: FileList | null) => {
    if (!list || !list.length) return;
    setWarn("");
    const valid = await validate(Array.from(list));
    if (valid.length) onAdd(valid);
  };

  return (
    <>
      <label
        className={clsx(styles.upload_zone, {
          [styles.upload_zone_drag]: isDragOver,
        })}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          className={styles.upload_input}
          accept={ACCEPT}
          multiple
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <div className={styles.upload_main}>
          Перетащите файл сюда
          <br />
          или <span>выберите с устройства</span>
        </div>
        <div className={styles.upload_format}>
          PNG, JPG, PDF • до {MAX_SIZE_MB} МБ
        </div>
      </label>

      {warn && <div className={styles.warn}>{warn}</div>}

      {files.length > 0 && (
        <div className={styles.previews}>
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} className={styles.preview}>
              {file.type.startsWith("image/") ? (
                <img src={previews[index]} alt={file.name} />
              ) : (
                <div className={styles.preview_file}>PDF</div>
              )}
              <button
                type="button"
                className={styles.remove_preview}
                aria-label="Удалить файл"
                onClick={() => onRemove(index)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
